import { useRef, useState } from "react";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { addToDate, getLastMonday, isDateAfter } from "@miplatform/utils";

export const useCapacityTrackerListViewModel = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const queryRef = useRef(null);
  const debouncedIsLoadingTimeoutRef = useRef(null);
  const listContainerRef = useRef(null);
  const initialStartDate = useRef(getLastMonday());
  const maxEndDate = useRef(addToDate(initialStartDate.current, 55, "days"));
  const [debouncedIsLoading, setDebouncedIsLoading] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [capacities, setCapacities] = useState({});

  const getSummaries = async () => {
    const response = await taskService.orderTemplateCapacities.getSummary({
      query: queryRef.current,
    });
    if (response.status === HTTP_STATUSES.OK) {
      // Create a date-keyed map to deduplicate entries by date
      // when fetching potentially overlapping date ranges
      setSummaries((prev) => {
        const existingEntries = new Map(prev.map((item) => [item.date, item]));

        response.data.forEach((newItem) => {
          existingEntries.set(newItem.date, newItem);
        });
        return Array.from(existingEntries.values());
      });
    }
  };

  const getCapacities = async () => {
    const response = await taskService.orderTemplateCapacities.getDetails({
      query: queryRef.current,
    });

    if (response.status === HTTP_STATUSES.OK) {
      setCapacities((prev) => ({
        ...prev,
        ...response.data.reduce(
          (acc, { orderTemplateId, orderTemplateShortName, orderTemplateName, data }) => {
            data.forEach(({ date, ...rest }) => {
              if (!acc[date]) {
                acc[date] = [];
              }
              acc[date].push({ orderTemplateId, orderTemplateShortName, orderTemplateName, ...rest });
            });
            return acc;
          },
          {}
        ),
      }));
    }
  };

  const shouldFetchMore = () => {
    const container = listContainerRef.current;
    if (!container) {
      return false;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const remainingScroll = scrollWidth - scrollLeft - clientWidth;
    const threshold = clientWidth * 0.9;

    return remainingScroll <= threshold || scrollWidth <= container.clientWidth;
  };

  const getInitialCapacities = () => {
    queryRef.current = null;
    getCapacityList();
  };

  const getCapacityList = () => {
    if (debouncedIsLoading) {
      return;
    }
    setDebouncedIsLoading(true);
    if (!queryRef.current) {
      queryRef.current = {
        from: initialStartDate.current.toISOString(),
        to: addToDate(initialStartDate.current, 13, "days").toISOString(),
      };
    } else {
      const newFrom = addToDate(queryRef.current.to, 1, "day");
      const newTo = addToDate(newFrom, 13, "days");
      if (isDateAfter(newTo, maxEndDate.current)) {
        setDebouncedIsLoading(false);
        return;
      }

      queryRef.current = {
        from: newFrom.toISOString(),
        to: newTo.toISOString(),
      };
    }

    Promise.all([getSummaries(), getCapacities()]).finally(() => {
      if (debouncedIsLoadingTimeoutRef.current) {
        clearTimeout(debouncedIsLoadingTimeoutRef.current);
      }
      debouncedIsLoadingTimeoutRef.current = setTimeout(() => {
        setDebouncedIsLoading(false);

        if (shouldFetchMore()) {
          getCapacityList();
        }
      }, 1000);
    });
  };

  return {
    isLoading: isLoading || debouncedIsLoading,
    capacities,
    summaries,
    listContainerRef,
    getInitialCapacities,
    getCapacityList,
  };
};
